import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

type Code = {
  code: string; label: string | null;
  maxUses: number; usedCount: number;
  expiresAt: string | null; revokedAt: string | null;
  url: string;
  status: 'active' | 'expired' | 'revoked' | 'used_up';
  createdAt: string;
};

async function getJSON<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'include' });
  if (r.status === 401) throw new Error('UNAUTH');
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

export default function AdminInvites() {
  const qc = useQueryClient();
  const meQ = useQuery({
    queryKey: ['session'],
    queryFn: () => getJSON<{ isAdmin: boolean }>('/api/session'),
    retry: false,
  });
  const codesQ = useQuery({
    queryKey: ['invites'],
    queryFn: () => getJSON<{ codes: Code[] }>('/api/admin/invite-codes'),
    enabled: !!meQ.data?.isAdmin,
  });
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [copied, setCopied] = useState<string | null>(null);
  const [createErr, setCreateErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/admin/invite-codes', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label || null, max_uses: maxUses, expires_in_days: expiresInDays }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || body.error || `HTTP ${r.status}`);
      }
      return r.json() as Promise<{ url: string; code: string }>;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invites'] }); setLabel(''); setCreateErr(null); },
    onError: (e: Error) => { setCreateErr(e.message); },
  });

  const revoke = useMutation({
    mutationFn: async (code: string) => {
      const r = await fetch(`/api/admin/invite-codes/${encodeURIComponent(code)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('revoke failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });

  const purge = useMutation({
    mutationFn: async (code: string) => {
      const r = await fetch(`/api/admin/invite-codes/${encodeURIComponent(code)}?purge=1`, { method: 'DELETE' });
      if (!r.ok) throw new Error('delete failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });

  useEffect(() => { if (copied) { const t = setTimeout(() => setCopied(null), 1200); return () => clearTimeout(t); } }, [copied]);

  if (meQ.isLoading) return <div style={{ padding: 40 }}>Loading…</div>;
  if (meQ.isError) return <Navigate to="/login?next=/admin/invites" replace />;
  if (!meQ.data?.isAdmin) return <Navigate to="/" replace />;

  return (
    <div style={{ maxWidth: 880, margin: '40px auto', padding: '0 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h1 className="serif" style={{ fontSize: 32, fontWeight: 400, margin: 0 }}>Invite codes</h1>
        <Link to="/admin/users" style={{ fontSize: 13, color: 'var(--pri)' }}>Users →</Link>
      </div>
      <p style={{ color: 'var(--ink2)', marginBottom: 32, fontSize: 14 }}>
        Generate a code, copy the URL, send it to a friend. They'll use it to register.
      </p>

      <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 8, padding: 20, marginBottom: 32 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', marginBottom: 6 }}>Label (optional)</div>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Friend1"
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 4, boxSizing: 'border-box' }} />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', marginBottom: 6 }}>Max uses</div>
            <input type="number" min={1} max={100} value={maxUses} onChange={(e) => setMaxUses(parseInt(e.target.value) || 1)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 4, boxSizing: 'border-box' }} />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', marginBottom: 6 }}>Expires (days)</div>
            <input type="number" min={1} max={365} value={expiresInDays} onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 7)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 4, boxSizing: 'border-box' }} />
          </label>
          <button className="btn accent" onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
        {createErr && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 13 }}>
            Couldn't create: {createErr}
            {createErr.toLowerCase().includes('admin') && (
              <> · Try <a href="/login?next=/admin/invites" style={{ color: '#991b1b', textDecoration: 'underline' }}>signing in again</a> to refresh your admin status.</>
            )}
          </div>
        )}
      </div>

      {codesQ.isLoading && <div>Loading codes…</div>}
      {codesQ.data?.codes && (
        <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--bg)' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 14px' }}>Label</th>
                <th style={{ textAlign: 'left', padding: '10px 14px' }}>Code</th>
                <th style={{ textAlign: 'left', padding: '10px 14px' }}>Used / Max</th>
                <th style={{ textAlign: 'left', padding: '10px 14px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '10px 14px' }}></th>
              </tr>
            </thead>
            <tbody>
              {codesQ.data.codes.map((c) => (
                <tr key={c.code} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: '10px 14px' }}>{c.label || <span style={{ color: 'var(--ink3)' }}>—</span>}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>{c.code}</td>
                  <td style={{ padding: '10px 14px' }}>{c.usedCount} / {c.maxUses}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em',
                      background: c.status === 'active' ? '#d1fae5' : '#fee2e2',
                      color: c.status === 'active' ? '#065f46' : '#991b1b',
                    }}>{c.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {c.status === 'active' ? (
                      <>
                        <button onClick={() => { navigator.clipboard.writeText(c.url); setCopied(c.code); }}
                          className="btn" style={{ marginRight: 6 }}>
                          {copied === c.code ? 'Copied!' : 'Copy URL'}
                        </button>
                        <button onClick={() => { if (confirm('Revoke this code?')) revoke.mutate(c.code); }}
                          className="btn">Revoke</button>
                      </>
                    ) : (
                      <button onClick={() => { if (confirm('Permanently delete this code?')) purge.mutate(c.code); }}
                        className="btn">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
              {codesQ.data.codes.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--ink3)' }}>No invite codes yet. Create one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 24 }}><Link to="/" style={{ color: 'var(--pri)', fontSize: 13 }}>← Back to app</Link></div>
    </div>
  );
}
